import { forwardRef, memo, useMemo } from 'react';

import { faChevronsLeft } from '@fortawesome/pro-solid-svg-icons';
import { useMatches } from 'react-router-dom';
import { styled } from 'styled-components';

import { useEvent } from 'core/hooks';
import { FlatIconButton, Icon } from 'core/ui';
import { equalsInsensitive, hasText } from 'core/utils';

import { UserRoles } from 'features/auth/constants';
import { useCurrentUser } from 'features/auth/hooks/useCurrentUser';
import { useSessionLocation } from 'features/location/hooks/useSessionLocation';
import { useUserSettings } from 'features/settings/hooks/useUserSettings';
import { SettingsHelpers } from 'features/settings/services/settings-helpers';
import { WellKnownSettingName } from 'features/settings/types';

import { NAVBAR_DESKTOP_PREFERRED_WIDTH, NAVBAR_LINKS, NAVBAR_MINI_WIDTH, NAVBAR_MOBILE_PREFERRED_WIDTH, NAVIGATION_LINK_IDS } from '../constants';
import { NavbarLinkItem } from '../types/NavbarLinkItem';
import { NavbarLink } from './NavbarLink';

type NavigationSidebarContentProps = {
  className?: string;
  mode: 'dialog' | 'mini' | 'full';
  expandedItemIds: string[];
  onDismiss?: () => void;
  onToggleClick?: () => void;
  onExpandItemChange?: (itemId: string, expanded: boolean) => void;
};

export const NavigationSidebarContent = memo(
  forwardRef<HTMLDivElement, NavigationSidebarContentProps>(({ className, mode, expandedItemIds, onDismiss, onToggleClick, onExpandItemChange }, ref) => {
    const matches = useMatches();

    const { sessionLocation } = useSessionLocation();
    const { userSettings, reactPatientGridPage, reactExams, reactUploadPage, queryRetrieve, legacyBaseUrl } = useUserSettings(false);
    const { currentUser } = useCurrentUser(false);

    const items = useMemo(() => {
      const newItems = NAVBAR_LINKS.map(cloneNavbarLinkItem);

      for (const item of newItems) {
        if ('selected' in item && matches.some((match) => match.id === item.id.toString())) {
          item.selected = true;
        }
        if ('expanded' in item && expandedItemIds.includes(item.id.toString())) {
          item.expanded = true;
        }

        if ('children' in item) {
          for (const child of item.children) {
            if ('selected' in child && matches.some((match) => match.id === child.id.toString())) {
              child.selected = true;
            }
            if ('expanded' in child && expandedItemIds.includes(child.id.toString())) {
              child.expanded = true;
            }
          }
        }
      }

      return newItems;
    }, [expandedItemIds, matches]);

    const filteredItems = useMemo(() => {
      // Return nothing until the user and settings have been fetched
      if (userSettings == null || currentUser == null || legacyBaseUrl == null) return [];

      let newItems = [...items];

      // Remove "Admin" section for non-admins.
      if (typeof currentUser === 'object' && ![UserRoles.ADMIN, UserRoles.CUSTOMER_ADMIN].includes(currentUser.role!)) {
        newItems = newItems.filter((i) => i.id !== NAVIGATION_LINK_IDS.ADMIN);
      }

      // Remove "Exams", "Upload Exam", and "Bulk Upload" for physicians
      if (currentUser === 'anonymous' || equalsInsensitive(currentUser.role, UserRoles.PHYSICIAN)) {
        newItems = newItems.filter((i) => ![NAVIGATION_LINK_IDS.EXAMS, NAVIGATION_LINK_IDS.UPLOAD_EXAM, NAVIGATION_LINK_IDS.BULK_UPLOAD].includes(i.id));
      }

      // Remove "Bulk Upload" for customers
      if (currentUser === 'anonymous' || equalsInsensitive(currentUser.role, UserRoles.CUSTOMER)) {
        newItems = newItems.filter((i) => i.id !== NAVIGATION_LINK_IDS.BULK_UPLOAD);
      }

      // Remove "Portal Settings" for everyone but system administrators
      if (currentUser === 'anonymous' || !currentUser.isSystemAdmin) {
        newItems = newItems.filter((i) => i.id !== NAVIGATION_LINK_IDS.PORTAL_SETTINGS);
      }

      // Remove query retrieve if the user doesn't have the setting
      if (currentUser === 'anonymous' || !queryRetrieve) {
        newItems = newItems.filter((i) => i.id !== NAVIGATION_LINK_IDS.QUERY_RETRIEVE);
      }

      // Filter out items with no valid children
      newItems = newItems.filter((item) => !('children' in item) || item.children.length > 0);

      // Change the links for certain items based on the user's settings
      const homePageUrl = SettingsHelpers.findSettingByName(userSettings, WellKnownSettingName.HomePageUrl, false);

      // Change the home link to the user's home page if it is set.
      if (homePageUrl && hasText(homePageUrl.value)) {
        const absoluteUrl: string =
          homePageUrl.value.startsWith('http://') || homePageUrl.value.startsWith('https://')
            ? homePageUrl.value
            : new URL(homePageUrl.value, window.location.origin).toString();

        const homeItemIndex = newItems.findIndex((i) => 'id' in i && i.id === NAVIGATION_LINK_IDS.HOME);

        if (homeItemIndex >= 0) {
          newItems[homeItemIndex] = {
            icon: newItems[homeItemIndex].icon,
            id: NAVIGATION_LINK_IDS.HOME,
            text: 'Home',
            title: 'Home',
            href: absoluteUrl,
          };
        }
      }

      // Figure out link to use for "Patients" item based on settings.
      const patientItemIndex = newItems.findIndex((i) => 'id' in i && i.id === NAVIGATION_LINK_IDS.PATIENTS);
      const patientItem = newItems[patientItemIndex];

      if (!reactPatientGridPage && patientItem != null && 'selected' in patientItem) {
        const newHref = new URL(legacyBaseUrl + '/Patient').toString();
        newItems[patientItemIndex] = {
          id: patientItem.id,
          icon: patientItem.icon,
          text: patientItem.text,
          title: patientItem.title,
          href: newHref,
        };
      }

      // Figure out link to use for "Exams" item based on settings.
      const examsItemIndex = newItems.findIndex((i) => 'id' in i && i.id === NAVIGATION_LINK_IDS.EXAMS);
      const examsItem = newItems[examsItemIndex];
      if (!reactExams && examsItem != null && 'selected' in examsItem) {
        const newHref = new URL(legacyBaseUrl + '/Exams/Index2').toString();
        newItems[examsItemIndex] = {
          id: examsItem.id,
          icon: examsItem.icon,
          text: examsItem.text,
          title: examsItem.title,
          href: newHref,
        };
      }

      // Figure out link to use for the "Upload Exam" item based on settings.
      const uploadExamsIndex = newItems.findIndex((i) => 'id' in i && i.id === NAVIGATION_LINK_IDS.UPLOAD_EXAM);
      const uploadExamsItem = newItems[uploadExamsIndex];
      if (!reactUploadPage && uploadExamsItem != null && 'selected' in uploadExamsItem) {
        const newHref = new URL(legacyBaseUrl + '/UploadExam').toString();
        newItems[uploadExamsIndex] = {
          id: uploadExamsItem.id,
          icon: uploadExamsItem.icon,
          text: uploadExamsItem.text,
          title: uploadExamsItem.title,
          href: newHref,
        };
      }

      // Add "sessionLocationId" query parameter to certain items if necessary.
      if (sessionLocation?.id != null) {
        const uploadItemIndex = newItems.findIndex((i) => 'id' in i && i.id === NAVIGATION_LINK_IDS.UPLOAD_EXAM);
        const uploadItem = { ...newItems[uploadItemIndex] };
        if (uploadItem != null && 'to' in uploadItem) {
          uploadItem.to = `${uploadItem.to}?sessionLocationId=${sessionLocation.id}`;
          newItems[uploadItemIndex] = uploadItem;
        }
      }

      return newItems;
    }, [currentUser, items, legacyBaseUrl, queryRetrieve, reactExams, reactPatientGridPage, reactUploadPage, sessionLocation?.id, userSettings]);

    const handleExpandSectionClick = useEvent((routeId: number) => {
      onExpandItemChange?.(routeId.toString(), !expandedItemIds.includes(routeId.toString()));
    });

    const handleLinkClick = useEvent(() => {
      // Only dismiss the navigation sidebar when in dialog mode because that is rendered as a modal and we don't want it to continue to cover the page.
      if (mode === 'dialog') {
        onDismiss?.();
      }
    });

    return (
      <StyledComponentDiv ref={ref} $mode={mode} id="navigation-sidebar-content" className={className}>
        {mode === 'mini' || mode === 'full' ? (
          <StyledDesktopToggleDiv $mode={mode}>
            <StyledDesktopToggleButton type="button" onClick={onToggleClick} $mode={mode}>
              <Icon icon={faChevronsLeft} fixedWidth={false} />
            </StyledDesktopToggleButton>
          </StyledDesktopToggleDiv>
        ) : (
          <StyledMobileDismissDiv>
            <FlatIconButton onClick={onDismiss}>
              <Icon icon={faChevronsLeft} />
            </FlatIconButton>
          </StyledMobileDismissDiv>
        )}
        <StyledListDiv>
          {filteredItems.map((item) => (
            <NavbarLink
              key={item.id}
              item={item}
              mode={mode}
              expandedItemIds={expandedItemIds}
              onExpandClick={handleExpandSectionClick}
              onLinkClick={handleLinkClick}
            />
          ))}
        </StyledListDiv>
      </StyledComponentDiv>
    );
  }),
);

NavigationSidebarContent.displayName = 'NavigationSidebarContent';

function cloneNavbarLinkItem(item: NavbarLinkItem): NavbarLinkItem {
  return {
    ...item,
    ...('children' in item ? { children: item.children.map(cloneNavbarLinkItem) } : {}),
  };
}

const StyledComponentDiv = styled.div<{ $mode: 'dialog' | 'mini' | 'full' }>`
  position: fixed;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: min-content 1fr;
  background-color: ${({ theme }) => theme.colors.palette.blues[5]};
  color: ${({ theme }) => theme.colors.palette.white};
  overscroll-behavior: contain;

  width: ${({ $mode }) =>
    $mode === 'dialog'
      ? `min(${NAVBAR_MOBILE_PREFERRED_WIDTH}px, 100dvw)`
      : $mode === 'mini'
        ? `${NAVBAR_MINI_WIDTH}px`
        : `min(${NAVBAR_DESKTOP_PREFERRED_WIDTH}px, 100dvw)`};
  height: 100dvh;
`;

const StyledDesktopToggleDiv = styled.div<{ $mode: 'mini' | 'full' }>`
  display: grid;
  background-color: ${({ theme }) => theme.colors.palette.white};
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  padding: 0 0 1px 0;
  height: 43px;

  width: ${({ $mode }) => ($mode === 'mini' ? `${NAVBAR_MINI_WIDTH}px` : `min(${NAVBAR_DESKTOP_PREFERRED_WIDTH}px, 100dvw)`)};
`;

const StyledDesktopToggleButton = styled.button<{ $mode: 'mini' | 'full' }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  justify-items: end;
  align-items: center;
  padding: 0 12px 0 0;
  border: none;
  background-color: ${({ theme }) => theme.colors.palette.blues[5]};
  color: ${({ theme }) => theme.colors.palette.white};
  border-top-right-radius: 6px;

  .icon-container {
    rotate: ${({ $mode }) => ($mode === 'mini' ? '-180deg' : '0deg')};
  }
`;

const StyledMobileDismissDiv = styled.div`
  display: flex;
  justify-content: end;
  padding-bottom: 2px;

  .icon-container {
    margin: 5px 0;
    color: ${({ theme }) => theme.colors.palette.white};
  }
`;

const StyledListDiv = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-rows: min-content;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 20px; // Desktop browsers show a tooltip on the bottom left of the screen when a link is hovered.  This gives enough space to avoid the tooltip from covering up any links in the navbar if the screen is short.
  overscroll-behavior: contain;
`;
